import { ChevronDown } from "lucide-react";
import { INTERVIEW_DEFINITION } from "@mycompbox/shared";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/**
 * InterviewRoadmap (ENG-144) — the section roadmap for the deeper epic-request
 * interview.
 *
 * PURELY PRESENTATIONAL. It renders {@link INTERVIEW_DEFINITION} from
 * `@mycompbox/shared` (ENG-143) — the single source of truth the machine daemon
 * also drives — so the web user sees the FULL depth (all sections, which are
 * required, and the intent + representative questions behind each) before/while
 * they answer. It never changes the job payload or writes any turn; the daemon
 * still asks the questions and answers still flow through the job mailbox.
 *
 * Collapsible, collapsed by default: the full section list is long and is
 * reference material, not something the user needs open while answering — so it
 * starts collapsed and expands on demand. `defaultOpen` lets callers (and tests)
 * render it expanded.
 */
export function InterviewRoadmap({
  className,
  defaultOpen = false,
}: {
  className?: string;
  defaultOpen?: boolean;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className={cn("space-y-3", className)}>
      <CollapsibleTrigger className="group flex w-full items-start justify-between gap-2 text-left">
        <div>
          <h3 className="text-sm font-semibold">What the interview covers</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            The formulator walks through these sections one question at a time.
            Required sections shape the epic request; the rest add depth.
          </p>
        </div>
        <ChevronDown
          aria-hidden
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="space-y-2">
          {INTERVIEW_DEFINITION.map((section, index) => (
            <li
              key={section.key}
              className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium">
                  {index + 1}. {section.title}
                </span>
                <Badge
                  className={cn(
                    "shrink-0 text-[10px] font-medium",
                    section.required
                      ? "bg-purple-900/30 text-purple-300"
                      : "border border-white/[0.06] bg-white/[0.04] text-muted-foreground"
                  )}
                >
                  {section.required ? "Required" : "Optional"}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{section.purpose}</p>
              {section.followUps.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {section.followUps.map((question) => (
                    <li
                      key={question}
                      className="text-[11px] text-muted-foreground/80"
                    >
                      &ndash; {question}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}
